/**
 * src/api/bcdrApi.js
 *
 * Thin client for the bcdr-write Azure Function — the dedicated per-record
 * write path for the BC/DR module (ADR-0006,
 * docs/decisions/0006-bcdr-per-record-storage.md, BCA-03). Mirrors
 * assetApi.js's / findingsApi.js's shape: one wrapper per action, each
 * returning the function's response body so callers can reconcile local
 * state without a full reload. `options` (e.g. `{ keepalive: true }`) passes
 * straight through to azurePost — used by BCDRIncidentModeModal's debounced
 * note writes so a save flushed on tab close/navigation can outlive the
 * document (mirrors findingsApi.js's teardown-flush use of the same option).
 */

import { azurePost } from './azureClient';

async function callBcdrWrite(action, body, tenantId, options = {}) {
  return azurePost(
    `/api/bcdr-write?action=${encodeURIComponent(action)}`,
    { ...body, tenantId },
    options
  );
}

/** Create-or-update a BC/DR plan by plan.id. Returns { ok, plan }. */
export async function saveBcdrPlanRecord(plan, tenantId, options) {
  return callBcdrWrite('save-plan', { plan }, tenantId, options);
}

/** Delete one BC/DR plan by id. Returns { ok }. */
export async function deleteBcdrPlanRecord(planId, tenantId, options) {
  return callBcdrWrite('delete-plan', { planId }, tenantId, options);
}

/** Create-or-update an ICT recovery runbook by runbook.id. Returns { ok, runbook }. */
export async function saveBcdrRunbookRecord(runbook, tenantId, options) {
  return callBcdrWrite('save-runbook', { runbook }, tenantId, options);
}

/** Delete one runbook by id. Returns { ok }. */
export async function deleteBcdrRunbookRecord(runbookId, tenantId, options) {
  return callBcdrWrite('delete-runbook', { runbookId }, tenantId, options);
}

/** Create-or-update a runbook execution (incident-mode run) by run.id. Returns { ok, run }. */
export async function saveBcdrRunRecord(run, tenantId, options) {
  return callBcdrWrite('save-run', { run }, tenantId, options);
}

/** Delete one runbook run by id. Returns { ok }. */
export async function deleteBcdrRunRecord(runId, tenantId, options) {
  return callBcdrWrite('delete-run', { runId }, tenantId, options);
}

/** Create-or-update a backup register entry by backup.id. Returns { ok, backup }. */
export async function saveBcdrBackupRecord(backup, tenantId, options) {
  return callBcdrWrite('save-backup', { backup }, tenantId, options);
}

/** Delete one backup entry by id. Returns { ok }. */
export async function deleteBcdrBackupRecord(backupId, tenantId, options) {
  return callBcdrWrite('delete-backup', { backupId }, tenantId, options);
}

/** Create-or-update a DR test by drTest.id. Returns { ok, drTest }. */
export async function saveBcdrDrTestRecord(drTest, tenantId, options) {
  return callBcdrWrite('save-drtest', { drTest }, tenantId, options);
}

/** Delete one DR test by id. Returns { ok }. */
export async function deleteBcdrDrTestRecord(drTestId, tenantId, options) {
  return callBcdrWrite('delete-drtest', { drTestId }, tenantId, options);
}

/** Create-or-update a pen test by penTest.id. Returns { ok, penTest }. */
export async function saveBcdrPenTestRecord(penTest, tenantId, options) {
  return callBcdrWrite('save-pentest', { penTest }, tenantId, options);
}

/** Delete one pen test by id. Returns { ok }. */
export async function deleteBcdrPenTestRecord(penTestId, tenantId, options) {
  return callBcdrWrite('delete-pentest', { penTestId }, tenantId, options);
}
